import { createContext, useContext, type ReactNode } from 'react';

/**
 * The parts of the shop's chrome a merchant can click their way into.
 *
 * Chrome is not in the section list - it is worn by every page, and its
 * settings live under Theme rather than under Sections - so a merchant who
 * wants to reword the announcement bar, swap the logo or restyle the footer
 * has nothing in the tree to reach for. They point at the thing itself
 * instead, and the customizer answers by opening the Theme tab on the group
 * that owns it.
 *
 * A name per part rather than a callback per part: a header variant only has
 * to say *what* it is drawing, and the customizer decides what clicking it
 * does. `footer` is on this list for the panel's sake rather than the
 * context's - the customizer renders the footer itself, so it frames that one
 * without a variant having to opt in.
 */
export type ChromeTarget = 'announcement' | 'logo' | 'footer';

export interface ChromeEditing {
  /**
   * The customizer's own selection overlay, for a chrome part to render inside
   * itself. Absolutely positioned, so a part opts in by adding `relative` and
   * nothing else: a wrapper element around a logo would become the flex or
   * grid child in a family's masthead and move the wordmark off its centre.
   */
  overlay: (target: ChromeTarget, label: string) => ReactNode;
}

const ChromeEditingContext = createContext<ChromeEditing | null>(null);

export const ChromeEditingProvider = ChromeEditingContext.Provider;

/**
 * The overlay for one chrome part, or nothing at all outside the customizer -
 * a shopper's header is a header, not an editor.
 */
export function useChromeOverlay(target: ChromeTarget, label: string): ReactNode {
  const editing = useContext(ChromeEditingContext);
  return editing ? editing.overlay(target, label) : null;
}
